import classnames from 'classnames';
import css from 'styled-jsx/css';
import PropTypes from 'prop-types';

import { colors } from '../../common/Theme';

import Destinations from './Destinations';
import Travelers from './Travelers';
import Pricing from './Pricing';

const MAX_WIDTH = 1000;
const LEFT_MARGIN = 50;
const DESKTOP_BREAKPOINT = 550;
const STEP_COUNTER_SIZE = '40px';

const styles = css`
  .simulation {
    flex-grow: 1;
  }
  .banner {
    background-color: ${colors.ywgPrimary};
    color: white;
    padding: 12px 0 102px 12px;
  }
  @media (min-width: ${MAX_WIDTH + 2 * LEFT_MARGIN}px) {
    .banner {
      padding-left: 50px;
    }
  }
  .title {
    font-size: 2.25rem;
    margin-bottom: 40px;
  }
  .steps {
    display: flex;
    flex-grow: 1;
    max-width: ${MAX_WIDTH}px;
    counter-reset: step;
    list-style: none;
    padding: 0;
    margin: 0;
  }
  .step {
    display: flex;
    font-size: 0.875rem;
    font-weight: bold;
    flex-basis: 33.33%;
    text-align: center;
    counter-increment: step;
    justify-content: center;
    align-items: center;
    padding: 12px 0;
    opacity: 0.5;
  }
  .step::before {
    display: flex;
    justify-content: center;
    align-items: center;
    content: counter(step);
    color: ${colors.ywgSecondary};
    background: white;
    border-radius: 50%;
    width: ${STEP_COUNTER_SIZE};
    height: ${STEP_COUNTER_SIZE};
  }
  .step--active {
    border-top: 4px solid white;
    opacity: 1;
  }
  .step--checked::before {
    content: '✔';
  }
  .step-label {
    display: none;
  }
  @media (min-width: ${DESKTOP_BREAKPOINT}px) {
    .step::before {
      margin-right: 16px;
    }
    .step-label {
      display: inline;
    }
  }
`;

const NEGATIVE_MARGIN = '-102px';
const childrenStyles = css.resolve`
  .form {
    display: flex;
    margin-top: ${NEGATIVE_MARGIN};
    margin-bottom: 100px;
    width: 100%;
    max-width: ${MAX_WIDTH}px;
    flex-direction: column;
  }
  .form--continue {
    margin-bottom: 0;
  }
  @media (min-width: ${MAX_WIDTH + 2 * LEFT_MARGIN}px) {
    .form {
      padding-left: ${LEFT_MARGIN}px;
    }
  }
  .action,
  .continue {
    min-width: 204px;
  }

  .continue {
    margin: 0;
  }

  .title {
    margin: 0 0 26px;
  }

  .panel {
    background: white;
    padding: 12px;
  }
  @media (min-width: ${MAX_WIDTH + 2 * LEFT_MARGIN}px) {
    .panel {
      padding: 40px 114px;
    }
  }

  .bottom-actions {
    display: flex;
    margin: 16px;
    justify-content: space-between;
    align-items: center;
  }

  .bottom-actions--end {
    justify-content: flex-end;
  }

  .return-link {
    color: ${colors.ywgNavy};
    font-size: 1.125rem;
    vertical-align: middle;
  }

  .return-link::before {
    display: inline-block;
    margin-right: 8px;
    width: 24px;
    height: 21px;
    content: '';
    background: url('/static/images/arrow.svg') center / contain no-repeat;
    vertical-align: middle;
  }
`;

const steps = [
  {
    id: 'destinations',
    num: 0,
    label: 'Vos destinations'
  },
  {
    id: 'travelers',
    num: 1,
    label: 'Les voyageurs'
  },
  {
    id: 'pricing',
    num: 2,
    label: 'Votre tarif'
  }
];

const Simulation = ({ step, profile, countries, prices, quote }) => {
  const currentStep = steps.filter(item => item.id === step)[0];
  return (
    <section className="simulation">
      <div className="banner">
        <h1 className="title">
          Simulez votre voyage et calculez votre assurance
        </h1>
        <ol className="steps">
          {steps.map((item, i) => (
            <li
              key={`simulation-steps-item-${item.id}`}
              className={classnames('step', {
                'step--checked': i < currentStep.num,
                'step--active': step === item.id
              })}
            >
              <span className="step-label">{item.label}</span>
            </li>
          ))}
        </ol>
      </div>
      {step === steps[0].id ? (
        <Destinations
          className={childrenStyles.className}
          countries={countries}
          prices={prices}
          quote={quote}
        />
      ) : null}
      {step === steps[1].id ? (
        <Travelers
          className={childrenStyles.className}
          countries={countries}
          quote={quote}
          profile={profile}
        />
      ) : null}
      {step === steps[2].id ? (
        <Pricing
          className={childrenStyles.className}
          countries={countries}
          quote={quote}
        />
      ) : null}
      <style jsx>{styles}</style>
      {childrenStyles.styles}
    </section>
  );
};

Simulation.propTypes = {
  step: PropTypes.oneOf(steps.map(step => step.id)).isRequired,
  countries: PropTypes.array.isRequired,
  prices: PropTypes.object.isRequired,
  profile: PropTypes.object,
  quote: PropTypes.object.isRequired
};

export default Simulation;
